import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { buildClassName } from '@bluebeam/build-class-name';
import { useDataAttributes } from '@bluebeam/react-hooks';
import { useLocale } from '@bluebeam/react-i18n';
import { GeneralIcons } from '@bluebeam/react-skyline-art';
import { InfoTooltip } from '@bluebeam/react-skyline-info-tooltip';
import { Label } from '@bluebeam/react-skyline-label';
import { Popover } from '@bluebeam/react-skyline-popover';
import { HiddenSelect, useSelect } from '@react-aria/select';
import { useRef } from 'react';
import { mergeProps, useButton, useFocusRing, useOverlay, useOverlayPosition } from 'react-aria';
import { Item, useSelectState } from 'react-stately';
import DropdownListbox from './DropdownListbox';
import DropdownListboxOption from './DropdownListboxOption';
const DropdownField = (props) => {
    const { autoComplete, 'aria-label': ariaLabel, className, error, options, label, id, isDisabled, isSubmitting, isTouched, name, onBlur, onChange, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    placeholder, popoverMaxHeight, required, tooltip, value, ...rest } = props;
    const availableOptions = options.map((option) => _jsx(Item, { children: option.label }, option.value));
    const maxHeight = options.length <= 4 ? 56 * options.length : popoverMaxHeight ?? 252;
    const dataAttributes = useDataAttributes(rest);
    const shouldDisplayError = Boolean(error && isTouched);
    const selectRef = useRef(null);
    const buttonRef = useRef(null);
    const listboxRef = useRef(null);
    const popoverRef = useRef(null);
    const { focusProps, isFocusVisible } = useFocusRing();
    const selectState = useSelectState({
        children: availableOptions,
        label,
        onSelectionChange: (key) => {
            onChange(key.toString());
        },
        selectedKey: value,
        isRequired: required,
        errorMessage: shouldDisplayError ? error : undefined,
        validationState: shouldDisplayError ? 'invalid' : 'valid',
    });
    const { labelProps, triggerProps, valueProps, menuProps, errorMessageProps } = useSelect({
        ...availableOptions,
        'aria-label': ariaLabel,
        autoComplete: autoComplete,
        selectRef,
        onChange,
        label,
        maxHeight,
        validationState: shouldDisplayError ? 'invalid' : 'valid',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }, selectState, buttonRef);
    const { buttonProps } = useButton({
        ...triggerProps,
        onBlur,
        isDisabled: isSubmitting || isDisabled,
    }, buttonRef);
    const { writingDirection } = useLocale();
    const minWidth = buttonRef.current?.clientWidth || 160;
    const maxWidth = window.innerWidth -
        (writingDirection === 'rtl'
            ? window.innerWidth - buttonRef.current?.offsetLeft - minWidth
            : buttonRef.current?.offsetLeft) || window.innerWidth;
    const overlayProps = {
        style: {
            maxWidth: maxWidth - 20, // don't bleed all the way to the viewport edge
            minWidth,
            ...useOverlayPosition({
                isOpen: selectState.isOpen,
                offset: 2,
                overlayRef: popoverRef,
                targetRef: buttonRef,
                placement: writingDirection === 'rtl' ? 'bottom right' : 'bottom left',
                containerPadding: 0,
                maxHeight,
            }).overlayProps.style,
        },
        ...useOverlay({
            isDismissable: true,
            isOpen: selectState.isOpen,
            onClose: selectState.close,
            shouldCloseOnBlur: true,
        }, popoverRef).overlayProps,
    };
    return (_jsxs("div", { id: id, ...dataAttributes, children: [label && (_jsxs(Label, { className: "mb-1 block leading-4", ...labelProps, children: [required ? `${label}*` : label, tooltip && _jsx(InfoTooltip, { tooltip: tooltip })] })), _jsx(HiddenSelect, { state: selectState, triggerRef: buttonRef, label: label, name: name }), _jsx("button", { type: "button", ...mergeProps(buttonProps, focusProps), ref: buttonRef, className: buildClassName('inline-flex rounded-lg border-solid', 'skyline-text-body-1 leading-5 text-black', 'cursor-pointer disabled:cursor-default', 'w-full bg-white px-4 py-[7px]', 'disabled:border-black/20 disabled:bg-white disabled:text-black/20', 'focus-visible:outline-none', className, selectState.isOpen || isFocusVisible
                    ? 'border-primary-blue shadow-primary-blue border-[1px]'
                    : 'border-darker-gray border-[1px]', shouldDisplayError ? 'border-alerts-error-red -m-px border-2' : ''), children: _jsxs("span", { ...valueProps, className: "flex w-full items-center justify-between gap-x-2 leading-5", children: [_jsx("span", { className: "truncate", children: value && selectState.selectedItem?.rendered }), _jsx(GeneralIcons.ChevronIcon, { direction: selectState.isOpen ? 'up' : 'down', size: "24px", className: "shrink-0" })] }) }), shouldDisplayError && (_jsx("span", { ...errorMessageProps, className: "skyline-text-body-2 text-alerts-error-red mt-1 block leading-4", children: error })), selectState.isOpen && (_jsx(Popover, { className: "shadow-[0px_2px_8px_0px_rgba(110,111,112,0.16)]", ref: popoverRef, ...overlayProps, children: _jsx(DropdownListbox, { ref: listboxRef, state: selectState, ...menuProps, style: { maxHeight }, children: options
                        .filter((option) => !option.isHidden)
                        .map((option) => {
                        return (_jsx(DropdownListboxOption, { state: selectState, value: option.value, children: option.label }, option.value));
                    }) }) }))] }));
};
export default DropdownField;
//# sourceMappingURL=DropdownField.js.map